import type { ScenarioSpec } from "./types";
import { officeUnderAttackScenario as OUA } from "./office-under-attack.scenario";

/**
 * Reception-terminal briefing on incident reporting.
 * The film is the only item across the top. The game is Office Under Attack
 * episode 10 (personal laptop) followed straight into episode 11 (the incident
 * report). Not on the public library until it is published.
 */
const VIDEO = "/cdn/sim/awareness-videos/incident-reporting/incident-reporting.mp4";
const STILL = "/cdn/sim/awareness-videos/incident-reporting/wide/17-report-button.jpg";

function beat(id: string) {
  const found = OUA.beats.find((item) => item.id === id);
  if (!found) throw new Error(`incident-reporting missing beat ${id}`);
  return found;
}

function device(id: string) {
  const found = OUA.devices.find((item) => item.id === id);
  if (!found) throw new Error(`incident-reporting missing device ${id}`);
  return found;
}

export const incidentReportingScenario: ScenarioSpec = {
  version: 1,
  id: "incident-reporting",
  title: "Report an incident",
  summary: {
    goal: "Learn what counts as a security incident and how to report it straight away with what happened, when, what is affected and what you already did.",
    youDo: "Watch a film, take a break on your own laptop, follow what happens at your desk, then file a ticket in the IT service desk and answer four questions.",
    duration: "Core 4-7 min, incident game adds 8-11 min",
    tags: ["incident_reporting", "ransomware", "personal_devices", "unsafe_downloads"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Report a security incident as soon as you notice it, with what happened, when, and what is affected",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: OUA.voiceAttackers,
  scene: OUA.scene,

  devices: [
    {
      id: "incident_terminal",
      kind: "breachbrief",
      config: {
        noticeTag: "INCIDENT",
        noticeKicker: "SECURITY AWARENESS",
        title: "Report an incident",
        offLabel: "Switch on the welcome terminal",
        voiceId: "onyx",
        progressKey: "incident-reporting",
        idleImage: STILL,
        videoFirst: true,
        lines: [
          "An incident is anything that puts company systems or information at risk: a strange download, a locked screen, a message you should not have opened.",
          "Report it as soon as you notice. Say what happened, when it happened, and what is affected. Do not wait until you are certain, and do not try to fix it alone.",
        ],
        hubIntro: "Learn what to report, then practise handling an incident.",
        hubVideo: {
          title: "Reporting an incident",
          blurb: "What to report, and what a useful report contains.",
          src: VIDEO,
          poster: STILL,
        },
        exercisesKicker: "1 interactive exercise",
        exercisesLabel: "Practise",
        exercises: [
          {
            id: "incident-game",
            label: "Play the incident",
            blurb: "A download on your own laptop reaches the company. Go back to your desk and write the report.",
            key: "game",
            completionBeatId: "b_cam_back",
            icon: "chapters",
            imageUrl: "/intro/img/office-under-attack-card.webp",
            actionLabel: "Play",
            doneLabel: "Done",
          },
        ],
        cardsTitle: "Practise",
        cardsIntro: "",
        cards: [],
        finishLabel: "Finish the briefing",
        finishHint: "Continue to the quiz",
        finishEarlyHint: "You can finish now, or watch the film and play the incident first.",
      },
    },
    device("personal_laptop"),
    device("incident_ticket"),
  ],

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Report an incident",
      kicker: "Reception",
      guide: {
        title: "Start",
        body: "You start at an unattended reception in the NorthBay office. The welcome terminal explains what counts as an incident and lets you play one through, starting with a quick break on your own laptop at 5 PM.",
      },
    },
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_hub",
      type: "device",
      deviceId: "incident_terminal",
      guide: {
        title: "Reception terminal",
        body: "Switch on the welcome terminal. The film and the incident are on the screen.",
      },
      completeOn: { action: "continue" },
    },
    beat("b_ep10_title"),
    beat("b_cam_personallaptop"),
    beat("b_cam_personallaptop_close"),
    beat("b_personallaptop"),
    {
      id: "b_ir_download_safe",
      type: "teach",
      title: "You called it risky",
      teach: {
        outcome: "safe",
        body:
          "A browser game that needs a separate launcher doesn't add up, and the company VPN was still connected. Closing that page keeps the problem off the laptop and away from the company drives.",
        remember: "A game that runs in the browser never needs an installer.",
        continueLabel: "See what would have happened",
        whatIf: "If you had run the launcher, this is the incident you would now be reporting.",
      },
    },
    beat("b_ep11_title"),
    beat("b_cam_incident"),
    beat("b_ransom_takeover"),
    beat("b_ransom_spread"),
    beat("b_ransom_teach_model"),
    beat("b_ransom_leaksite"),
    beat("b_incident_ticket"),
    beat("b_ir_consequence_trap"),
    beat("b_ir_teach_trap"),
    beat("b_ir_teach_perfect"),
    beat("b_ir_teach_misfiled"),
    { id: "b_cam_back", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check what you would report",
      quiz: [
        {
          id: "q_first",
          title: "Question 1",
          prompt: "Your screen starts locking files after a download on your own laptop. What do you do first?",
          options: [
            { id: "a", label: "Pay the ransom yourself so work can continue", icon: "x" },
            { id: "b", label: "Disconnect, then tell IT/Security what happened and when", correct: true, icon: "check" },
            { id: "c", label: "Restart the computer and hope the files come back", icon: "alert" },
          ],
          rationale: "Paying or restarting on your own can make the incident worse. Report what happened, when, and what is affected.",
        },
        {
          id: "q_useful",
          title: "Question 2",
          prompt: "What belongs in the incident report?",
          options: [
            { id: "a", label: "Who you think should be blamed", icon: "x" },
            { id: "b", label: "What happened, when, what is affected, and what you already did", correct: true, icon: "check" },
            { id: "c", label: "A short note that it is nothing serious and already sorted", icon: "alert" },
          ],
          rationale: "IT can only act on the facts you give them: the timeline, the systems, and the steps you already took.",
        },
        {
          id: "q_vpn",
          title: "Question 3",
          prompt: "The bad download was on your personal laptop, but the company VPN was still connected. Why does that matter?",
          options: [
            { id: "a", label: "It does not. A personal laptop cannot reach company files", icon: "x" },
            { id: "b", label: "The open VPN let the malware reach the same shared drives as your work PC", correct: true, icon: "check" },
            { id: "c", label: "It only matters if you type your password a second time", icon: "alert" },
          ],
          rationale: "A personal device has no path into the company until a work connection, such as a VPN, is still open.",
        },
        {
          id: "q_vague",
          title: "Question 4",
          prompt: "You file a report that leaves out the time and says not to worry. What is the risk?",
          options: [
            { id: "a", label: "None. A calm note is better than a detailed one", icon: "x" },
            { id: "b", label: "IT may treat it as minor and leave the problem connected for longer", correct: true, icon: "check" },
            { id: "c", label: "The report is ignored only if you used the wrong form", icon: "alert" },
          ],
          rationale: "A vague or downplayed report delays the response. No timeline and no affected systems means no fast containment.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_hub" },
    { from: "b_hub", to: "b_ep10_title", on: "game" },
    { from: "b_ep10_title", to: "b_cam_personallaptop" },
    { from: "b_cam_personallaptop", to: "b_cam_personallaptop_close" },
    { from: "b_cam_personallaptop_close", to: "b_personallaptop" },
    { from: "b_personallaptop", to: "b_ep11_title" },
    { from: "b_personallaptop", to: "b_ir_download_safe", on: "safe" },
    { from: "b_ir_download_safe", to: "b_ep11_title" },
    { from: "b_ep11_title", to: "b_cam_incident" },
    { from: "b_cam_incident", to: "b_ransom_takeover" },
    { from: "b_ransom_takeover", to: "b_ransom_spread" },
    { from: "b_ransom_spread", to: "b_ransom_teach_model" },
    { from: "b_ransom_teach_model", to: "b_ransom_leaksite" },
    { from: "b_ransom_leaksite", to: "b_incident_ticket" },
    { from: "b_incident_ticket", to: "b_ir_consequence_trap", on: "risky" },
    { from: "b_incident_ticket", to: "b_ir_teach_perfect", on: "perfect" },
    { from: "b_ir_consequence_trap", to: "b_ir_teach_trap" },
    { from: "b_ir_teach_trap", to: "b_cam_back" },
    { from: "b_ir_teach_perfect", to: "b_cam_back" },
    { from: "b_incident_ticket", to: "b_ir_teach_misfiled", on: "misfiled" },
    { from: "b_ir_teach_misfiled", to: "b_cam_back" },
    { from: "b_cam_back", to: "b_hub" },
    { from: "b_hub", to: "b_quiz", on: "finish" },
  ],

  initialBeatId: "b_intro",
};
